
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import HeroSection from '../components/HeroSection';
import Tokenomics from '../components/Tokenomics';
import Team from '../components/Team';
import ComicGallery from '../components/ComicGallery';
import ImageGallery from '../components/ImageGallery';
import Footer from '../components/Footer';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 212, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(0, 212, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 212, 255, 0); }
`;

const glitch = keyframes`
  0% { text-shadow: 2px 0 #ff00ff, -2px 0 #00ffff; }
  25% { text-shadow: -2px 0 #ff00ff, 2px 0 #00ffff; }
  50% { text-shadow: 2px 0 #ff00ff, -2px 0 #00ffff; }
  75% { text-shadow: -2px 0 #ff00ff, 2px 0 #00ffff; }
  100% { text-shadow: 2px 0 #ff00ff, -2px 0 #00ffff; }
`;

const sphereFloat = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-10px) rotate(90deg); }
  50% { transform: translateY(-5px) rotate(180deg); }
  75% { transform: translateY(-15px) rotate(270deg); }
  100% { transform: translateY(0px) rotate(360deg); }
`;

const sphereGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(138, 43, 226, 0.5), 0 0 40px rgba(138, 43, 226, 0.3); }
  50% { box-shadow: 0 0 30px rgba(255, 20, 147, 0.6), 0 0 60px rgba(255, 20, 147, 0.4); }
  100% { box-shadow: 0 0 20px rgba(138, 43, 226, 0.5), 0 0 40px rgba(138, 43, 226, 0.3); }
`;

const orbitRing1 = keyframes`
  0% { transform: rotateX(60deg) rotateY(0deg); }
  100% { transform: rotateX(60deg) rotateY(360deg); }
`;

const orbitRing2 = keyframes`
  0% { transform: rotateX(-60deg) rotateY(0deg); }
  100% { transform: rotateX(-60deg) rotateY(360deg); }
`;

const orbitRing3 = keyframes`
  0% { transform: rotateX(0deg) rotateY(0deg); }
  100% { transform: rotateX(0deg) rotateY(360deg); }
`;

const SphereTooltip = styled.div`
  position: fixed;
  bottom: 110px;
  right: 15px;
  z-index: 1001;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  
  .text-line-1 {
    color: #ffffff;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 4px;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.9);
    letter-spacing: 1px;
    background: rgba(0, 0, 0, 0.7);
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid var(--primary);
  }

  .text-line-2 {
    color: #ffffff;
    font-size: 14px;
    font-weight: bold;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.9);
    letter-spacing: 1px;
    background: rgba(0, 0, 0, 0.7);
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid var(--accent);
  }

  @media (max-width: 768px) {
    bottom: 90px;
    right: 10px;
    
    .text-line-1 {
      font-size: 14px;
    }
    
    .text-line-2 {
      font-size: 12px;
    }
  }
`;

const FloatingSphereButton = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7A1FD6 0%, #E60D87 50%, #3B0060 100%);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
  animation: ${sphereFloat} 6s ease-in-out infinite, ${sphereGlow} 3s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  perspective: 100px;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%),
      linear-gradient(-45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%),
      linear-gradient(135deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    border-radius: 50%;
    animation: ${sphereFloat} 8s ease-in-out infinite reverse;
  }

  .orbit-ring {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }

  .orbit-ring-1 {
    width: 90px;
    height: 90px;
    border: 3px solid transparent;
    background: linear-gradient(45deg, #0066ff, #ffdd00, #0099ff, #ffaa00) border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${orbitRing1} 10s linear infinite;
    box-shadow: 0 0 15px rgba(0, 102, 255, 0.6);
  }

  .orbit-ring-2 {
    width: 100px;
    height: 100px;
    border: 2px solid transparent;
    background: linear-gradient(135deg, #ffdd00, #0066ff, #ffaa00, #0099ff) border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${orbitRing2} 15s linear infinite;
    box-shadow: 0 0 12px rgba(255, 221, 0, 0.5);
  }

  .orbit-ring-3 {
    width: 110px;
    height: 110px;
    border: 2px solid transparent;
    background: linear-gradient(90deg, #0099ff, #ffaa00, #0066ff, #ffdd00) border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${orbitRing3} 18.75s linear infinite;
    box-shadow: 0 0 10px rgba(0, 153, 255, 0.4);
  }

  &:hover {
    transform: scale(1.1);
    animation-play-state: paused;
    box-shadow: 0 0 40px rgba(138, 43, 226, 0.8), 0 0 80px rgba(255, 20, 147, 0.6);
    
    .orbit-ring {
      animation-play-state: paused;
      box-shadow: 0 0 25px rgba(0, 102, 255, 0.8);
    }
  }

  &:hover + ${SphereTooltip} {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    bottom: 20px;
    right: 20px;
    
    .orbit-ring-1 {
      width: 80px;
      height: 80px;
    }
    
    .orbit-ring-2 {
      width: 90px;
      height: 90px;
    }
    
    .orbit-ring-3 {
      width: 100px;
      height: 100px;
    }
  }
`;

const SnowdenContainer = styled.div`
  background: linear-gradient(135deg, #0a0a1a 0%, #121228 100%);
  color: #fff;
  font-family: 'Courier New', monospace;
  position: relative;
  overflow-x: hidden;
`;

const DataStream = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
  z-index: 0;
`;

const TerminalEffect = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 212, 255, 0.03) 0%,
    rgba(0, 212, 255, 0.01) 50%,
    rgba(0, 212, 255, 0.03) 100%
  );
  background-size: 100% 4px;
  pointer-events: none;
  z-index: 0;
  animation: ${fadeIn} 2s ease-in;
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 992px) {
    padding: 60px 5%;
  }
`;

const MissionStatement = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 40px;
  margin: 60px auto;
  max-width: 900px;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 1s ease-out;

  p {
    font-size: 1.3rem;
    line-height: 1.8;
    text-align: center;
    margin-bottom: 20px;
    color: #e0e7ff;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #00d4ff;
      font-weight: normal;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const WhitepaperSection = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 40px;
  margin: 60px auto;
  max-width: 900px;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 1s ease-out;
  text-align: center;

  p {
    font-size: 1.3rem;
    line-height: 1.8;
    color: #e0e7ff;
    margin-bottom: 20px;
  }

  a {
    color: #ff00ff;
    text-decoration: none;
    font-weight: bold;
    transition: color 0.3s ease;

    &:hover {
      color: #00d4ff;
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const MintSection = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 40px;
  margin: 60px auto;
  max-width: 600px;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 1s ease-out;
  text-align: center;

  h3 {
    color: #00d4ff;
    font-size: 1.8rem;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.3rem;
    line-height: 1.8;
    color: #e0e7ff;
    margin-bottom: 20px;
  }

  a {
    background: linear-gradient(45deg, #ff00ff, #00d4ff);
    color: #000;
    border: none;
    padding: 15px 30px;
    border-radius: 5px;
    font-size: 1.2rem;
    font-weight: bold;
    text-decoration: none;
    display: inline-block;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(0, 212, 255, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin: 80px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ImageCard = styled.div`
  background: rgba(0, 0, 0, 0.6);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 212, 255, 0.2);
  animation: ${fadeIn} 0.8s ease-out;
  animation-delay: ${props => props.delay || '0s'};

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 212, 255, 0.3);
    border-color: rgba(255, 0, 255, 0.4);
  }

  img {
    width: 100%;
    height: auto;
    display: block;
    border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  }

  .card-content {
    padding: 20px;

    h3 {
      color: #00d4ff;
      margin-bottom: 10px;
      font-size: 1.4rem;
    }

    p {
      color: #b8c2d8;
      line-height: 1.6;
      font-size: 1rem;
    }

    .social-link {
      display: inline-block;
      margin-top: 15px;
      color: #ff00ff;
      text-decoration: none;
      font-weight: bold;
      transition: color 0.3s ease;

      &:hover {
        color: #00d4ff;
        text-decoration: underline;
      }
    }
  }

  .video-container {
    position: relative;
    padding-top: 56.25%;
    width: 100%;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    }
  }
`;

const PriceTicker = styled.div`
  background: linear-gradient(90deg, #121228 0%, #1a1a4a 100%);
  border-radius: 8px;
  padding: 20px;
  margin: 50px auto;
  max-width: 800px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  animation: ${pulse} 2s infinite, ${fadeIn} 1s ease-out;

  .price-item {
    flex: 1;
    min-width: 200px;
    text-align: center;

    .label {
      color: #b8c2d8;
      font-size: 0.9rem;
      margin-bottom: 5px;
    }

    .value {
      color: #00ff88;
      font-size: 1.5rem;
      font-weight: bold;
      font-family: 'Courier New', monospace;
    }

    .change {
      font-size: 0.9rem;
      margin-top: 5px;

      &.positive {
        color: #00ff88;
      }

      &.negative {
        color: #ff5555;
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;

    .price-item {
      min-width: 100%;
    }
  }
`;

const SectionHeader = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin: 80px 0 50px;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 3px;
  position: relative;
  animation: ${glitch} 2s infinite alternate;

  &::after {
    content: '';
    display: block;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, #ff00ff, #00d4ff);
    margin: 20px auto 0;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin: 60px 0 40px;
  }
`;

const StatsSection = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 40px;
  margin: 60px auto;
  max-width: 900px;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 1s ease-out;
  text-align: center;

  p {
    font-size: 1.3rem;
    line-height: 1.8;
    color: #e0e7ff;
    margin: 10px 0;

    strong {
      color: #00d4ff;
      font-weight: normal;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const PartneredExchangesSection = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 40px;
  margin: 60px auto;
  max-width: 600px;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 1s ease-out;
  text-align: center;

  a {
    display: block;
    transition: all 0.3s ease;
  }

  img {
    width: 100%;
    max-width: 300px;
    height: auto;
    display: block;
    margin: 0 auto;
    border-radius: 5px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 5px 15px rgba(0, 212, 255, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Snowden = () => {
  const [kaspaPrice, setKaspaPrice] = useState(0.09657);
  const [snowdnPrice, setSnowdnPrice] = useState(0);
  const [mmediaPrice, setMmediaPrice] = useState(0);
  const [change24h, setChange24h] = useState({ kaspa: '0.0%', snowdn: '0.0%', mmedia: '0.0%' });

  const handleSphereClick = () => {
    window.open('https://www.kaspasphere.com/', '_blank');
  };

  useEffect(() => {
    const fetchKaspaPrice = async () => {
      try {
        const response = await axios.get(
          'https://api.coingecko.com/api/v3/simple/price?ids=kaspa&vs_currencies=usd&include_24hr_change=true'
        );
        const kasPrice = response.data.kaspa.usd;
        const kasChange = response.data.kaspa.usd_24h_change.toFixed(1);
        setKaspaPrice(kasPrice);
        setSnowdnPrice(kasPrice * 0.21069);
        setMmediaPrice(kasPrice * 5.490645);
        setChange24h({
          kaspa: `${kasChange >= 0 ? '+' : ''}${kasChange}%`,
          snowdn: `${kasChange >= 0 ? '+' : ''}${kasChange}%`,
          mmedia: `${kasChange >= 0 ? '+' : ''}${kasChange}%`
        });
      } catch (error) {
        console.error('Error fetching Kaspa price:', error);
      }
    };

    fetchKaspaPrice();
    const interval = setInterval(fetchKaspaPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <SnowdenContainer>
      <DataStream />
      <TerminalEffect />
      
      <HeroSection
        title="$SNOWDN ON KASPA"
        subtitle="The Privacy-First, Decentralized Cryptocurrency Collectible Built on Proof-of-Work"
        bgImage="/assets/Kassnow.jpg"
        darkOverlay
      />
      
      <ContentSection>
        <MissionStatement>
          <p>
            <strong>Edward Snowden risked everything to expose mass surveillance.</strong> Now, we're leveraging blockchain technology to advocate for his fair repatriation, privacy rights and freedom.
          </p>
          <p>
            The $SNOWDN token isn't just a crypto collectible—it's a statement. Built on Kaspa's revolutionary BlockDAG technology, $SNOWDN represents the future of decentralized, discreetly secured, censorship-resistant value transfer.
          </p>
        </MissionStatement>

        <WhitepaperSection>
          <p>Our original Nonprofit KRC20 project, $SNOWDN, WHITEPAPER UPDATED 4/26/25</p>
          <p><a href="https://t.me/ModMediaNetwork/19/26" target="_blank" rel="noopener noreferrer">View Whitepaper</a></p>
        </WhitepaperSection>

        <PriceTicker>
          <div className="price-item">
            <div className="label">Kaspa Price</div>
            <div className="value">${kaspaPrice.toFixed(6)}</div>
            <div className={`change ${change24h.kaspa.startsWith('+') ? 'positive' : 'negative'}`}>
              {change24h.kaspa} (24h)
            </div>
          </div>
          <div className="price-item">
            <div className="label">$SNOWDN Price</div>
            <div className="value">${snowdnPrice.toFixed(6)}</div>
            <div className={`change ${change24h.snowdn.startsWith('+') ? 'positive' : 'negative'}`}>
              {change24h.snowdn} (24h)
            </div>
          </div>
          <div className="price-item">
            <div className="label">$MMEDIA Subscriber Utility Price</div>
            <div className="value">${mmediaPrice.toFixed(6)}</div>
            <div className={`change ${change24h.mmedia.startsWith('+') ? 'positive' : 'negative'}`}>
              {change24h.mmedia} (24h)
            </div>
          </div>
        </PriceTicker>

        <SectionHeader>Mint $SNOWDN</SectionHeader>
        <MintSection>
          <h3>Mint Your $SNOWDN Tokens</h3>
          <p>Join the movement by minting $SNOWDN tokens using the Kasware wallet.</p>
          <a href="https://kasware.xyz" target="_blank" rel="noopener noreferrer">Mint on Kasware</a>
        </MintSection>
        
        <ImageGrid>
          <ImageCard delay="0.2s">
            <div className="video-container">
              <iframe src="https://player.vimeo.com/video/1044232074?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" title="Snowden 12 Yr. Exile- Snowden Crypto in Sidebar"></iframe>
            </div>
            <div className="card-content">
              <p>Stay updated with the latest $SNOWDN project updates.</p>
            </div>
          </ImageCard>
        </ImageGrid>

        <SectionHeader>Stats</SectionHeader>
        <StatsSection>
          <p><strong>HOLDERS:</strong> 10,000+ Web3 Users</p>
          <p><strong>MINTED:</strong> 15,000 - 89% remains, still publicly minting!</p>
          <p><strong>AGE:</strong> OVER 1.5 YEARS SINCE LAUNCH</p>
        </StatsSection>

        <SectionHeader>Tokenomics</SectionHeader>
        <Tokenomics />
        
        <SectionHeader>Partnered Exchanges</SectionHeader>
        <PartneredExchangesSection>
          <a href="https://kaspa.com/tokens/marketplace/token/SNOWDN" target="_blank" rel="noopener noreferrer">
            <img src="/assets/kaspacom.jpg" alt="Kaspa Marketplace" />
          </a>
        </PartneredExchangesSection>
        
        <SectionHeader>Our Team</SectionHeader>
        <Team />
        
        <SectionHeader>Comic Series</SectionHeader>
        <ComicGallery />
        
        <SectionHeader>Gallery</SectionHeader>
        <ImageGallery />
      </ContentSection>

      <FloatingSphereButton onClick={handleSphereClick}>
        <div className="orbit-ring orbit-ring-1"></div>
        <div className="orbit-ring orbit-ring-2"></div>
        <div className="orbit-ring orbit-ring-3"></div>
      </FloatingSphereButton>
      
      <SphereTooltip>
        <div className="text-line-1">-$SPHERE DAO</div>
        <div className="text-line-2">COIN LAUNCH!</div>
      </SphereTooltip>
      
      <Footer />
    </SnowdenContainer>
  );
};

export default Snowden;
